<template>
    <v-chart
        class="col full-width full-height chart bg-white"
        :option="chartOptions"
        autoresize
        @click="handleClick"
    />
</template>
<script>
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { HeatmapChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    VisualMapComponent,
    GridComponent
} from 'echarts/components';

use([
    CanvasRenderer,
    HeatmapChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    VisualMapComponent,
    GridComponent
]);

import { formatDate } from '@utils/dateRangeFormatter.js';
import DataEntry from './dataentry';
import { colors } from 'quasar';
const { getPaletteColor } = colors;
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'DataEntryHeatMap',
    components: { VChart },
    emits: ['handleShowEvents'],
    props: {
        facility: {
            type: Number,
            required: true
        },
        chartData: {
            type: Object,
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        }
    },
    data() {
        return {
            assetIndexMap: {},
            visualMapMin: 0,
            visualMapMax: 100,
            chartGradientColor: {
                0: getPaletteColor('light-red'),
                1: getPaletteColor('light-red'),
                2: getPaletteColor('light-red'),
                3: getPaletteColor('light-yellow'),
                4: getPaletteColor('light-green')
            },
            assetAssignedYIndex: 0,
            xAxisLabels: new Set(),
            yAxisLabels: new Set(),
            titleColor: getPaletteColor('secondary')
        };
    },
    methods: {
        /**
         * Handle user click on the heatmap
         * @return {String}
         */
        handleClick(params) {
            const { value } = params.data;

            // third index in value array is the data entry percentage value
            const percentageValue = value[2];
            // if the data entry value for the cell is 100%, then disable the onClick popup
            if (percentageValue < 0 || percentageValue >= 100) {
                return;
            }

            // first index in value array is the selected date index
            const selectedDate = this.chartData.dateMap[value[0]];

            const selectedLabelName =
                this.chartData.yAxisLabels[params.value[1]];

            const selectedAssetId =
                this.chartData.assetMap.get(selectedLabelName);
            this.$emit('handleShowEvents', selectedAssetId, selectedDate);
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Determine if to show cell labels or not
         */
        showCellLabels() {
            // setting the max number of column values
            // that are allowed to show labels in the
            // heatmap. It is set to 3 days worth of columns
            // i.e. 3(days) * 25(hours/day)[01/xx/xx 00:00:00 - 02/xx/xx 00:00:00].
            //  Anything more than this will not be able to show the cell labels
            const maxAllowedColumns = 75;
            const numberOfChartMatrixColumns = this.chartData.xAxisLabels.size;
            if (numberOfChartMatrixColumns > maxAllowedColumns) {
                return false;
            }
            return true;
        },
        /**
         * Get formatted title of chart based on provided intervals and user locale
         * @return {String}
         */
        chartTitle() {
            return formatDate(
                this.chartData.dateInterval.start,
                this.chartData.dateInterval.end,
                this.userLocale
            );
        },
        chartOptions() {
            return {
                title: {
                    text: this.chartTitle,
                    textStyle: {
                        fontSize: 14,
                        fontWeight: 'normal',
                        color: this.titleColor
                    },
                    top: '0%',
                    left: 'center'
                },
                tooltip: {
                    // only show tooltip on desktop because
                    // the tooltip stays on top of the dialog on mobile
                    show: this.$q.platform.is.desktop,
                    position: 'top',
                    formatter: params => {
                        if (
                            params.data.value[2] !==
                            DataEntry.defaultPercentageValue
                        ) {
                            return `${params.seriesName}<br/>
                          ${params.name}: ${params.value[2]}%
                          `;
                        } else {
                            return `${params.seriesName}<br/>
                            ${params.name}: -
                            `;
                        }
                    }
                },
                grid: {
                    top: '6%',
                    left: 10,
                    right: 10,
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                xAxis: {
                    type: 'category',
                    data: Array.from(this.chartData.xAxisLabels),
                    splitArea: {
                        show: true
                    }
                },
                stateAnimation: {
                    duration: 0
                },
                yAxis: {
                    type: 'category',
                    data: Array.from(this.chartData.yAxisLabels),
                    splitArea: {
                        show: true
                    },
                    splitLine: {
                        width: 3,
                        show: true
                    },
                    axisLabel: {
                        interval: 0
                    }
                },
                gradientColor: this.chartGradientColor,
                visualMap: {
                    min: this.visualMapMin,
                    max: this.visualMapMax,
                    calculable: true,
                    orient: 'horizontal',
                    left: 'center',
                    bottom: '1%',
                    hoverLink: false
                },
                series: [
                    {
                        name: this.$t('Data Entry Performance'),
                        type: 'heatmap',
                        data: this.chartData.chartMatrix,
                        label: {
                            show: this.showCellLabels,
                            formatter: param =>
                                param.data.value[2] !==
                                DataEntry.defaultPercentageValue
                                    ? `${param.data.value[2]}%`
                                    : '-'
                        },
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowColor: 'rgba(0, 0, 0, 0.5)'
                            }
                        },
                        itemStyle: {
                            // cell color for default value
                            // in this case for '-'.
                            color: 'rgba(220,220,220,0.7)',
                            borderType: 'solid',
                            borderColor: 'white',
                            borderWidth: 1
                        }
                    }
                ]
            };
        }
    }
};
</script>

<style lang="scss" scoped>
.chart {
    border: 1px solid $grey-4;
    border-radius: 5px;
    padding: 15px 20px;
    height: 800px !important;
}
</style>
